import { DataGrid } from 'src/feature/data-grid/data-grid';
import { useDataGridSearchParams } from 'src/feature/data-grid/use-data-grid-search-params/use-data-grid-search-params';
import { ChargePointResponseModel } from 'src/core/model/charge-point.model';
import { Snackbar } from 'src/shared/snackbar/snackbar';
import { useTranslation } from 'react-i18next';
import { useGetPaginatedChargePointsQuery } from '@service/charge-point.service';
import { DataGridQueryModel } from '@model/data-grid.model';
import { useCallback, useState } from 'react';
import { GetChargePointsWithFiltersModel } from '@delmonicos/shared-sdk/libs/emsp/src/libs/shared/src/cpo-service/charge-point/models/get-charge-points-with-filters.model';
import { SortOrder } from '@delmonicos/shared-sdk/libs/emsp/src/libs/core/src/utils/mongo/pagination/models/pagination.model';
import { useChargePointColumn } from './use-charge-point-column';

type ChargePointDataGridProps = {
  readonly onGenerateQRCode: (chargePoint: ChargePointResponseModel) => void;
};

export function ChargePointDataGrid(
  props: ChargePointDataGridProps,
): React.JSX.Element {
  const { onGenerateQRCode } = props;
  const { t } = useTranslation();

  const columns = useChargePointColumn({
    onGenerateQRCode,
  });

  // PAGINATION ////////////////////////////////////////////////////////////////
  const { model: urlModel } = useDataGridSearchParams();
  const [model, setModel] = useState<DataGridQueryModel>(urlModel);

  const [params, setParams] = useState<GetChargePointsWithFiltersModel>(() => {
    const pageSize = model.paginate?.pageSize ?? 10;
    const page = model.paginate?.page ?? 0;

    return {
      sortField: 'chargePointId',
      sortOrder: SortOrder.DESC,
      limit: pageSize,
      offset: page * pageSize,
      textSearch: model.textSearch || undefined,
    };
  });

  const onModelChange = useCallback(
    (newModel: DataGridQueryModel) => {
      setModel(newModel);

      setParams((prev) => {
        const pageSize = newModel.paginate?.pageSize ?? prev.limit ?? 10;
        const page = newModel.paginate?.page ?? 0;

        return {
          ...prev,
          limit: pageSize,
          offset: page * pageSize,
          textSearch: newModel.textSearch || undefined,
        };
      });
    },
    [setModel],
  );

  // DATA FETCHING /////////////////////////////////////////////////////////////
  const {
    data: paginatedData,
    isFetching,
    isError,
  } = useGetPaginatedChargePointsQuery(params);

  return (
    <>
      <DataGrid
        getRowId={(row) => {
          return row.chargePointId;
        }}
        columns={columns}
        loading={isFetching}
        rows={paginatedData?.docs ?? []}
        rowCount={paginatedData?.totalDocs ?? 0}
        model={model}
        onModelChange={onModelChange}
      />
      {isError && (
        <Snackbar
          variant="error"
          message={t('error.get-list-charge-point-query')}
        />
      )}
    </>
  );
}
